/**
 * NPI (National Provider Identifier) validation using the Luhn-like check digit algorithm.
 * NPI is a 10-digit number; the last digit is a check digit.
 * @see https://www.cms.gov/Regulations-and-Guidance/HIPAA-Administrative-Simplification/NationalProvIdentStand
 */

const { ceil, floor } = Math;
const toInt = (s: string): number => Number.parseInt(s, 10);

const sumDigits = (int: number): number => {
    let sum = 0;
    while (int) {
        sum += int % 10;
        int = floor(int / 10);
    }
    return sum;
};

const doubleAlternate = (value: string, index?: number): number => {
    const pos = index ?? value.length - 1;
    let sum = 0;
    let alt = true;
    for (let i = pos; i >= 0; i--) {
        const cur = toInt(value.charAt(i));
        sum += alt ? sumDigits(cur * 2) : cur;
        alt = !alt;
    }
    return sum;
};

/**
 * Validates an NPI number using the standard check digit algorithm.
 * @param id - The NPI value to validate (string or number)
 * @param prefix - The 5-digit prefix (default '80840' for United States)
 * @returns true if the NPI is valid
 */
export function npiValid(id: string | number | null | undefined, prefix = '80840'): boolean {
    const prefixRegex = /^80\d{3}$/;
    const npiRegex = /^\d{10}$/;
    const usPrefixSum = 24; // Precalculated value for 80840 (United States)
    const npi = id?.toString() ?? '';
    const pre = prefix?.toString() ?? '';

    if (!npiRegex.test(npi) || !prefixRegex.test(pre)) {
        return false;
    }

    const check = toInt(npi.charAt(9));
    const npiSum = doubleAlternate(npi, 8);
    const preSum = pre === '80840' ? usPrefixSum : doubleAlternate(pre, 3);
    const total = npiSum + preSum;
    const totalCeil = ceil(total / 10) * 10;

    return totalCeil - total === check;
}

export default npiValid;
